/**
 * Chat marker (a status/separator line between messages) — ported from the
 * shadcn/ui registry (`radix-nova/marker`, fetched 2026-09-16) and
 * retokenized to Meridian. The file is ours from here on: it is not
 * synchronized with the registry (spec §8.0.1, D15).
 *
 * Replacements against the registry source: `cva` → an inline variant
 * dictionary; `Slot` from `@radix-ui/react-slot`; `cn` from `@/lib/utils`;
 * `text-muted-foreground` → `text-fg-muted`; `hover:text-foreground` →
 * `hover:text-fg`. `role="status" aria-live="polite"` for a "thinking…"
 * marker is the caller's to set — the container itself is presentational.
 */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

export type MarkerVariant = 'default' | 'separator' | 'border';

const MARKER_BASE =
  "group/marker relative flex min-h-4 w-full items-center gap-2 text-left text-sm text-fg-muted [&_svg:not([class*='size-'])]:size-4 [a]:underline [a]:underline-offset-3 [a]:hover:text-fg";

const MARKER_VARIANTS: Record<MarkerVariant, string> = {
  default: '',
  separator:
    'before:mr-1 before:h-px before:min-w-0 before:flex-1 before:bg-border after:ml-1 after:h-px after:min-w-0 after:flex-1 after:bg-border',
  border: 'border-b border-border pb-2',
};

function Marker({
  className,
  variant = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & {
  variant?: MarkerVariant;
  asChild?: boolean;
}) {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="marker"
      data-variant={variant}
      className={cn(MARKER_BASE, MARKER_VARIANTS[variant], className)}
      {...props}
    />
  );
}

function MarkerIcon({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="marker-icon"
      aria-hidden="true"
      className={cn("size-4 shrink-0 [&_svg:not([class*='size-'])]:size-4", className)}
      {...props}
    />
  );
}

function MarkerContent({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="marker-content"
      className={cn(
        'min-w-0 wrap-break-word group-data-[variant=separator]/marker:flex-none group-data-[variant=separator]/marker:text-center *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-fg',
        className
      )}
      {...props}
    />
  );
}

export { Marker, MarkerIcon, MarkerContent };
